/**
 * cookie-banner.js — GDPR consent banner integrated with
 * Shopify's Customer Privacy API.
 *
 * Flow:
 *   1. Wait for window.Shopify.customerPrivacy to be available
 *      (loadFeatures fallback for older storefronts).
 *   2. If the visitor's consent is undecided (any key === null), show banner.
 *   3. On Accept All / Reject All / Save preferences → call
 *      Shopify.customerPrivacy.setTrackingConsent(consent, callback)
 *      with the chosen values. This is the authoritative state Shopify
 *      reads when firing analytics/marketing pixels and is exposed as
 *      window.Shopify.customerPrivacy.visitorConsent.
 *
 * To reopen the banner (e.g. from a footer "Manage cookies" link):
 *   <button data-cookie-reopen>Manage cookies</button>
 */
(function () {
  if (window.__OS_COOKIE_BANNER__) return;
  window.__OS_COOKIE_BANNER__ = true;

  var CONSENT_KEYS = ['analytics', 'marketing', 'preferences'];
  var DECIDED_KEY = 'cookieBannerDecided:v1';

  function markDecided() {
    try { localStorage.setItem(DECIDED_KEY, '1'); } catch (e) {}
  }
  function clearDecided() {
    try { localStorage.removeItem(DECIDED_KEY); } catch (e) {}
  }
  function wasDecided() {
    try { return localStorage.getItem(DECIDED_KEY) === '1'; } catch (e) { return false; }
  }

  // ─── Customer Privacy API ─────────────────────────────────────────────
  function loadAPI(callback) {
    // Already loaded
    if (window.Shopify && window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
      return callback(null);
    }
    // Use Shopify.loadFeatures if available (older theme storefronts)
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      try {
        return window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) { callback(err || null); }
        );
      } catch (e) { return callback(e); }
    }
    // Shopify global not yet present — poll briefly
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (window.Shopify) { clearInterval(timer); loadAPI(callback); }
      else if (attempts > 60) { clearInterval(timer); callback(new Error('Shopify.customerPrivacy unavailable')); }
    }, 100);
  }

  function currentConsent() {
    var p = window.Shopify && window.Shopify.customerPrivacy;
    if (!p) return null;
    // Prefer the live getter — visitorConsent is also kept up-to-date by Shopify
    if (typeof p.currentVisitorConsent === 'function') {
      try { return p.currentVisitorConsent(); } catch (e) { /* fall through */ }
    }
    return p.visitorConsent || null;
  }

  function isDecided(consent) {
    if (!consent) return false;
    // All three keys must be a boolean (i.e. not null/undefined)
    return CONSENT_KEYS.every(function (k) {
      return consent[k] === true || consent[k] === false;
    });
  }

  function applyConsent(consent, onDone) {
    var p = window.Shopify && window.Shopify.customerPrivacy;
    if (!p || typeof p.setTrackingConsent !== 'function') {
      console.error('[cookieBanner] Customer Privacy API not ready; consent not propagated.');
      if (onDone) onDone(new Error('API not ready'));
      return;
    }
    p.setTrackingConsent(consent, function (err) {
      if (err) console.error('[cookieBanner] setTrackingConsent error:', err);
      if (onDone) onDone(err);
    });
  }

  // ─── UI handlers ──────────────────────────────────────────────────────
  function showBanner(banner) {
    banner.classList.remove('show-prefs');
    banner.setAttribute('aria-hidden', 'false');
    var firstBtn = banner.querySelector('[data-cookie-accept-all]');
    if (firstBtn && typeof firstBtn.focus === 'function') {
      // Defer focus so screen readers register the new role region
      setTimeout(function () { firstBtn.focus(); }, 50);
    }
  }

  function hideBanner(banner) {
    banner.setAttribute('aria-hidden', 'true');
    banner.classList.remove('show-prefs');
  }

  function populateToggles(banner, consent) {
    banner.querySelectorAll('input[data-cookie-key]').forEach(function (input) {
      var k = input.getAttribute('data-cookie-key');
      input.checked = consent && consent[k] === true;
    });
  }

  function init() {
    var banner = document.querySelector('[data-cookie-banner]');
    if (!banner) return;

    // Footer reopen link (e.g. <button data-cookie-reopen>Manage cookies</button>) —
    // forces the banner open regardless of previous decision.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-cookie-reopen]');
      if (!trigger) return;
      e.preventDefault();
      clearDecided();
      populateToggles(banner, currentConsent() || {});
      banner.classList.add('show-prefs');
      showBanner(banner);
    });

    // Accept all — note sale_of_data stays false. Othon Schmitt does not sell
    // customer data; GDPR forbids opt-in default for this category.
    var btnAccept = banner.querySelector('[data-cookie-accept-all]');
    if (btnAccept) btnAccept.addEventListener('click', function () {
      markDecided();
      applyConsent({ analytics: true, marketing: true, preferences: true, sale_of_data: false },
        function () { hideBanner(banner); });
    });

    // Reject all
    var btnReject = banner.querySelector('[data-cookie-reject-all]');
    if (btnReject) btnReject.addEventListener('click', function () {
      markDecided();
      applyConsent({ analytics: false, marketing: false, preferences: false, sale_of_data: false },
        function () { hideBanner(banner); });
    });

    // Manage preferences → show prefs view, populate toggles from current consent
    var btnManage = banner.querySelector('[data-cookie-manage]');
    if (btnManage) btnManage.addEventListener('click', function () {
      populateToggles(banner, currentConsent() || {});
      banner.classList.add('show-prefs');
    });

    // Prefs back
    var btnBack = banner.querySelector('[data-cookie-prefs-back]');
    if (btnBack) btnBack.addEventListener('click', function () {
      banner.classList.remove('show-prefs');
    });

    // Prefs save
    var btnSave = banner.querySelector('[data-cookie-prefs-save]');
    if (btnSave) btnSave.addEventListener('click', function () {
      var consent = { sale_of_data: false };
      CONSENT_KEYS.forEach(function (k) {
        var input = banner.querySelector('input[data-cookie-key="' + k + '"]');
        consent[k] = !!(input && input.checked);
      });
      markDecided();
      applyConsent(consent, function () { hideBanner(banner); });
    });

    // Decide whether to show banner on initial load.
    // localStorage flag is the authoritative "user has clicked a button on this device"
    // signal — it survives Shopify cookie expiration / ITP / 3rd-party cookie blocking,
    // which are common reasons the banner kept reappearing on every page.
    if (wasDecided()) return;

    loadAPI(function (err) {
      if (err) {
        // API didn't load — still show banner so user has *some* control,
        // but warn that choices won't propagate to Shopify pixels.
        console.warn('[cookieBanner] Customer Privacy API failed to load:', err.message || err);
        showBanner(banner);
        return;
      }
      var consent = currentConsent();
      if (!isDecided(consent)) showBanner(banner);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
